import React from 'react';
import styled, { css } from 'styled-components';
import ShipIcon from '@mui/icons-material/DirectionsBoat';
import { IconButton, Link, Paper, Typography, Box } from '@mui/material';
import moment from 'moment';

// constants
import { INSTRUCTOR_TIMETABLE, ROOM_TIMETABLE, SET_TIMETABLE } from '../../constants';
import { MOMENT_TIME_DISPLAY_FORMAT } from '../../constants/date_format';
import { colors } from '../../theme';

const GenerateDOM = (props) => {
    const { layouts, getInstructorTimetable, getSetTimetable, getRoomTimetable, week, timetableType } = props;

    const renderBaseLayoutComponent = (item) => {
        const {
            settings: { type, value },
        } = item;

        switch (type) {
            case 'available':
                return (
                    <AvailableSpot>
                        <div />
                    </AvailableSpot>
                );
            case 'initial':
                return (
                    <InitialSpot>
                        <div />
                    </InitialSpot>
                );

            case 'emptyCell':
                return <EmptyCell />;

            case 'weekday':
                return <Weekday children={value.slice(0, 3)} />;

            case 'text':
                return <Typography variant="body1" align="center" children={value} />;

            case 'time': {
                if (!value.isValid()) return null;

                const formattedValue = value.format(MOMENT_TIME_DISPLAY_FORMAT);

                const isFullHour = formattedValue.includes(':00');

                return <Time variant="caption" align="center" isFullHour={isFullHour} children={value.format(MOMENT_TIME_DISPLAY_FORMAT)} />;
            }
            case 'conflict':
                return <Conflict children={'Conflict(s)'} />;

            default:
        }
    };

    const renderTimeAndCourse = ({ timeIn, timeOut, course, sections, conflict }) => {
        return (
            <React.Fragment>
                <Typography variant="body2" align="center">
                    {' '}
                    {timeIn && moment(timeIn, 'HH:mm:ss').format(MOMENT_TIME_DISPLAY_FORMAT)} - {timeOut && moment(timeOut, 'HH:mm:ss').format(MOMENT_TIME_DISPLAY_FORMAT)}{' '}
                    <span>
                        {course.subject} {course.courseNumber} <span className="timetable-item-type">{renderClassType(sections)}</span>
                    </span>{' '}
                    {conflict != null && <span className="timetable-item-conflict-type">{renderConflictType(conflict)}</span>}
                </Typography>
            </React.Fragment>
        );
    };

    const renderConflictType = (conflict) => {
        let value = '';

        if (conflict.isInstructorConflict) value += '[IC]';

        if (conflict.isSetConflict) value += ' [SC]';

        if (conflict.isRoomConflict) value += ' [RC]';

        return value;
    };

    const renderClassType = (sections) => {
        if (Array.isArray(sections) && sections.length > 0) {
            return sections[0].classType.substring(0, 3); //class type is the same for all sections
        }
    };

    const renderSetTimetableComponents = ({ room, buildingCode, sections, instructors, sets, course }) => {
        return (
            <React.Fragment>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                    {renderRoomBuilding(room, buildingCode, course)}
                    {renderMultipleSections(sections)}
                </Box>
                {renderMultipleInstructors(instructors, course)}
                {sets && renderMultipleSets(sets)}
            </React.Fragment>
        );
    };

    const renderInstructorTimetableComponents = ({ room, buildingCode, sections, instructors, sets, objectID, id, course }) => {
        let instructorsToRender = [];

        //Avoid rendering the selected instructor on the instructor timetable
        // if (Array.isArray(instructors) && instructors.length > 0) { instructorsToRender = instructors.filter(i => i.instructorID !== objectID); }

        return (
            <React.Fragment>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                    {renderRoomBuilding(room, buildingCode, course)}
                    {renderMultipleSections(sections)}
                </Box>
                {renderMultipleInstructors(instructors, course)}
                {renderMultipleSets(sets)}
            </React.Fragment>
        );
    };

    const renderOfficeHourComponents = ({ officeHourNote }) => {
        return (
            <React.Fragment>
                <Typography variant="body2" align="center">
                    Office Hour
                </Typography>
                <Typography variant="body2" align="center">
                    Note: {officeHourNote}
                </Typography>
            </React.Fragment>
        );
    };

    const renderRoomTimetableComponents = ({ instructors, sections, sets, course }) => {
        return (
            <React.Fragment>
                <Box sx={{ mt: '3px' }}>{renderMultipleSections(sections)}</Box>
                {renderMultipleInstructors(instructors, course)}
                {renderMultipleSets(sets)}
            </React.Fragment>
        );
    };

    /*
     * Render Building and Room row
     * - can be null in Instructor and Set timetables
     */
    const renderRoomBuilding = (room, buildingCode, course) => {
        if (course.courseNumber === 'Hour Break') return;
        if (!room && !buildingCode) return <div>Room: N/A</div>;

        return (
            room &&
            buildingCode && (
                <div>
                    <StyledLink
                        variant="body2"
                        onClick={async () =>
                            getRoomTimetable &&
                            (await getRoomTimetable(
                                {
                                    id: room.roomID,
                                    roomNumber: room.roomNumber,
                                    buildingCode: buildingCode,
                                },
                                week,
                            ))
                        }
                    >
                        {buildingCode}-{room.roomNumber}
                    </StyledLink>
                </div>
            )
        );
    };

    /*
     * Render the Instructor row
     * - Could be null on Set and Room timetables
     */
    const renderMultipleInstructors = (instructors, course) => {
        if (course.courseNumber === 'Hour Break') return;
        if (!instructors) return <div>Instructor(s): N/A</div>;

        if (Array.isArray(instructors) && instructors.length === 0) return <div>Instructor(s): N/A</div>;
        return instructors
            .slice()
            .sort((a, b) => {
                return a.fullName.localeCompare(b.fullName, 'en-US', { sensitivity: 'base', ignorePunctuation: true });
            })
            .map((instructor, index) => (
                <StyledLink
                    key={instructor.instructorID + index}
                    variant="body2"
                    onClick={async () =>
                        getInstructorTimetable &&
                        (await getInstructorTimetable(
                            {
                                id: instructor.instructorID,
                                name: instructor?.fullName,
                            },
                            week,
                        ))
                    }
                >
                    {instructor && instructor.fullName}
                </StyledLink>
            ));
    };

    const renderMultipleSections = (sections) => {
        return (
            <Typography variant="body2" align="center">
                {sections && sections.map((section) => '(' + section.crn.toString() + ' - ' + section.enrolled + '/' + section.roomCap + ') ')}
            </Typography>
        );
    };

    /*
     * Render Set row
     * - Cannot be null on Instructor Timetables
     * - Can be null on Room Timetables
     */
    const renderMultipleSets = (sets) => {
        if (!sets) return;

        if (Array.isArray(sets) && sets.length === 0) return 'SET: N/A';

        return (
            <div>
                {sets.map((set, index) => (
                    // eslint-disable-next-line react/jsx-key
                    <StyledLink
                        variant="body2"
                        onClick={async () =>
                            await getSetTimetable(
                                {
                                    id: set.setID,
                                    setCode: set.setCode,
                                    departmentCode: set.departmentCode,
                                    isEditable: false,
                                },
                                week,
                            )
                        }
                    >
                        {set.displayCode || set.setCode}
                    </StyledLink>
                ))}
            </div>
        );
    };

    const renderEvent = (value) => {
        const { timeIn, timeOut, note } = value;

        return (
            <>
                <Typography variant="body2" align="center">
                    {moment(timeIn, 'HH:mm:ss').format(MOMENT_TIME_DISPLAY_FORMAT)} - {moment(timeOut, 'HH:mm:ss').format(MOMENT_TIME_DISPLAY_FORMAT)}
                </Typography>
                <Typography variant="body2" align="center" sx={{ fontWeight: 'bold' }}>
                    {note}
                </Typography>
            </>
        );
    };

    const renderTimetableComponents = (value) => {
        if (value.isEvent) return <div>{renderEvent(value)}</div>;
        return (
            <div
                className="droppable-element"
                draggable={true}
                unselectable="on"
                // this is a hack for firefox
                // Firefox requires some kind of initialization
                // which we can do by adding this attribute
                // @see https://bugzilla.mozilla.org/show_bug.cgi?id=568313
                onDragStart={(e) => e.dataTransfer.setData('text/plain', '')}
            >
                {/* TODO: Refactor using same logic as above */}
                {value.isEvent && renderEvent(value)}
                {!value.isOfficeHour && renderTimeAndCourse(value)}
                {value.isOfficeHour && renderOfficeHourComponents(value)}
                {timetableType === SET_TIMETABLE && renderSetTimetableComponents(value)}
                {timetableType === INSTRUCTOR_TIMETABLE && !value.isOfficeHour && renderInstructorTimetableComponents(value)}
                {timetableType === ROOM_TIMETABLE && renderRoomTimetableComponents(value)}
            </div>
        );
    };

    // Change cell colour classnames based on class data and classtype
    const getTimetableCellClassName = (classData) => {
        //default class name
        let className = 'timetable-item';

        // Identifying if INSTRUCTOR COURSE is lab or lecture(sections)
        if (classData.sections && classData.sections.length > 0 && classData.sections[0].classType === 'Lecture') className = 'timetable-item-lecture';

        if (classData.sections && classData.sections.length > 0 && classData.sections[0].classType === 'Lab') className = 'timetable-item-lab';

        if (classData.isOfficeHour) className = 'timetable-item-office-hour';

        if (classData.isHighlightable) className = 'isHighlightable';
        if (classData.conflict && classData.conflict.anyConflict) className = 'timetable-item-conflict';

        return className;
    };

    //FIXME: detect which layout needs to be used
    return layouts.lg.map((item) => {
        const {
            i,
            settings: { type, value: classData },
        } = item;

        return (
            <FixedPaddingPaper key={i} elevation={1}>
                {type !== 'timetable' && renderBaseLayoutComponent(item)}

                {type === 'timetable' && (
                    <TimetableItem>
                        <TimetableItemInner className={getTimetableCellClassName(classData)}>{renderTimetableComponents(classData)}</TimetableItemInner>
                    </TimetableItem>
                )}
            </FixedPaddingPaper>
        );
    });
};


const FixedPaddingPaper = styled(Paper)`
  padding: 0;
  display: flex;
  overflow: auto;
  flex-direction: column;
  border-radius: 0;
  box-shadow: none;
  background: transparent;
`;

const Time = styled(({ isFullHour, ...rest }) => <Typography {...rest} />)`
    && {
        display: flex;
        justify-content: center;
        align-items: center;
        /* width: 50%; */
        width: 100%;
        height: 100%;
        font-size: 13px;

        ${(props) =>
        !props.isFullHour &&
        `
        border-top: 2px solid #EFEFEF;
  
      `};

        &:before {
            content: '';
            position: absolute;
            z-index: 9999;
            left: 100%;
            top: 0;
            width: 100vw;
            height: 2px;
            background: #efefef;
            opacity: ${(props) => (props.isFullHour ? 0 : 1)};
        }
    }
`;

const Weekday = styled(({ children, ...rest }) => <Typography {...rest} children={children} />)`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: #efefef;
    color: ${colors.black};
    text-transform: uppercase;
    font-weight: 500 !important;

    &:before {
        content: '';
        position: absolute;
        z-index: 9999;
        left: -2px;
        top: 0;
        width: 2px;
        height: 200vh;
        background: #efefef;
        opacity: 1;
    }

    // Display bugfix

    &:after {
        content: '';
        ${(props) =>
        props.children === 'Friday' &&
        `
          position: absolute;
          z-index: 9999;
          right: 0;
          top: 0;
          width: 2px;
          height: 200vh;
          background: #EFEFEF;
          opacity: 1;
        `}
    }
`;

const TimetableItem = styled.div`
    height: 100%;
    width: 100%;
    padding-top: 2px;
    padding-right: 2px;

    .timetable-item {
        /* background-color: ${colors.white}; */
        /* opacity: 0.8; */
        background: repeating-linear-gradient(-45deg, #f9f9f9, #f9f9f9 3px, #fff 3px, #fff 5px);
    }

    .timetable-item-lab {
        background: #e5f3fc;
        .timetable-item-type {
            color: ${colors.blue.main};
        }
    }

    .timetable-item-lecture {
        background: #ebfaf3;
        .timetable-item-type {
            color: ${colors.green.main};
        }
    }

    .timetable-item-conflict {
        background: #ffed85;
    }

    .timetable-item-office-hour {
        background: #81c784;
    }

    /* .timetable-item-conflict-type {
        font-weight: bold;
    } */

    .isHighlightable {
        background: #ccffcc;
    }

    .timetable-item-event {
        background: #f9f9f9;
    }

    span {
        font-weight: bold;
    }
`;

const TimetableItemInner = styled.div`
    display: flex;
    justify-content: center;
    text-align: center;
    height: 100%;
    width: 100%;
    overflow: auto;
    /* box-shadow: 0 0 3px 0px rgba(0, 0, 0, 0.2); */

    > div {
        height: fit-content;
        padding: 4px;
        margin: auto 0;
    }

    p {
        line-height: 14px;
        color: #000;
    }

    a,
    button {
        color: #0085e6;
    }
`;

const Conflict = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: #fbebed;
    color: ${colors.red.main};
    text-transform: uppercase;
    font-size: 1rem;
    font-weight: bold;
`;

const StyledLink = styled(Link)`
    display: inline;
    text-align: center;
    width: 100%;
    padding-right: 7px;
    cursor: pointer;
`;

const EmptyCell = styled.div`
    /* width: 50%; */
    width: 100%;
    height: 100%;
    background: #efefef;

    &:after {
        content: '';
        position: absolute;
        z-index: 9999;
        left: 0;
        top: 0;
        width: 2px;
        height: 200vh;
        background: #efefef;
        opacity: 1;
    }
`;

const AvailableSpot = styled.div`
    width: 100%;
    height: 100%;
    padding-top: 2px;
    padding-right: 2px;

    > div {
        width: 100%;
        height: 100%;
        background: #95d295;
    }
`;

const InitialSpot = styled.div`
    width: 100%;
    height: 100%;
    padding-top: 2px;
    padding-right: 2px;

    > div {
        width: 100%;
        height: 100%;
        background: #d3d3d3;
    }
`;

export default GenerateDOM;
